import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VideocamIcon from "@mui/icons-material/Videocam";
import moment from "moment";
import { get_next_session_api } from "../DAL/Tutors/Tutors";
import { openSessionRoom, isInSession } from "../utils/sessionRoom";
import {
  surface, border, textPrimary, textSecondary, textMuted, accent, font,
  success, primaryButtonSx, easing,
} from "../layouts/schoolPortal/schoolPortalTheme";
import { prettyName } from "../pages/SchoolPortal/StudentRecord/recordTheme";

/**
 * "Your lesson is about to start", wherever you happen to be.
 *
 * WHY THIS EXISTS
 * Everything needed to join a lesson lived on the Teaching page — the button,
 * the "Join opens 12:30" label, all of it. So whether you made your lesson
 * depended on which page you were looking at when it started. An instructor on
 * Students got nothing: no countdown, no nudge. The room was open and nobody
 * was told.
 *
 * IT IS PERSISTENT ON PURPOSE.
 * Dismissing it hides it until the join window actually opens, and then it
 * comes back once — because "I saw it and carried on working" and "I am not
 * joining this lesson" are different things, and only the second one should be
 * able to silence the moment it matters. Dismissing it again after that sticks
 * for the rest of the session.
 *
 * POLLING, NOT SOCKETS. The endpoint answers null almost always and the answer
 * only changes on a clock, so a request a minute is both sufficient and far
 * less to go wrong than a socket that has to survive sleep and reconnection.
 */

const POLL_MS = 60000;
// Re-checked far more often than it is fetched: the countdown has to tick, and
// the window opening is a clock event rather than a server event.
const TICK_MS = 1000;

export default function SessionJoinPrompt() {
  const [session, setSession] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  // "seen while it was still counting down" vs "gone for good".
  const dismissedRef = useRef({ bookingId: null, afterOpen: false });
  const [, force] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await get_next_session_api();
      setSession(r?.code === 200 ? r.data : null);
    } catch (e) {
      // A failed poll must never take a page down with it.
      setSession(null);
    }
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(load, POLL_MS);
    const tick = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [load]);

  if (!session) return null;

  // Already in the room, in the other tab. Comes back by itself on leaving:
  // the room tab clears the marker and the one-second tick re-reads it.
  if (isInSession(session.bookingId)) return null;

  const opensAt = new Date(session.join.opensAt).getTime();
  const isOpen = session.join.open || now >= opensAt;

  // The window has closed. The endpoint stops returning the session too, but it
  // is polled once a minute, and a dead Join button for up to sixty seconds
  // after a lesson ends is a button that does nothing when pressed.
  const closesAt = session.join.closesAt ? new Date(session.join.closesAt).getTime() : null;
  if (closesAt && now > closesAt) return null;

  const d = dismissedRef.current;
  if (d.bookingId === String(session.bookingId)) {
    // Dismissed before it opened: allowed back exactly once, when it opens.
    if (!isOpen) return null;
    if (d.afterOpen) return null;
  }

  const dismiss = () => {
    dismissedRef.current = { bookingId: String(session.bookingId), afterOpen: isOpen };
    force((n) => n + 1);
  };

  const secondsAway = Math.max(0, Math.round((opensAt - now) / 1000));
  const countdown = secondsAway >= 60
    ? `${Math.ceil(secondsAway / 60)} min`
    : `${secondsAway}s`;

  const who = session.withName ? ` with ${session.withName}` : "";
  const startsIn = moment(session.startTime).fromNow();

  return (
    <>
      <Box
        sx={{
          position: "fixed", zIndex: 1300,
          right: { xs: 12, sm: 20 }, bottom: { xs: 12, sm: 20 },
          left: { xs: 12, sm: "auto" },
          width: { xs: "auto", sm: 340 },
          backgroundColor: surface,
          border: `1px solid ${isOpen ? accent : border}`,
          borderLeft: `3px solid ${isOpen ? accent : textMuted}`,
          borderRadius: "12px",
          boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
          p: 1.75,
          animation: `joinPromptIn 0.25s ${easing}`,
          "@keyframes joinPromptIn": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontFamily: font, fontSize: "10.5px", fontWeight: 800,
              letterSpacing: "0.07em", textTransform: "uppercase",
              color: isOpen ? accent : textSecondary,
            }}>
              {isOpen ? "Ready to join" : "Starting soon"}
            </Typography>
            <Typography sx={{
              fontFamily: font, fontSize: "14px", fontWeight: 700, color: textPrimary, mt: 0.2,
            }}>
              {prettyName(session.subject) || "Your session"}{who}
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary, mt: 0.15 }}>
              {moment(session.startTime).format("HH:mm")} · starts {startsIn}
            </Typography>
          </Box>
          <IconButton size="small" onClick={dismiss} sx={{ color: textMuted, mt: -0.5, mr: -0.5 }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {isOpen ? (
          <Button
            fullWidth
            startIcon={<VideocamIcon sx={{ fontSize: 17 }} />}
            onClick={() => openSessionRoom(session.bookingId)}
            sx={{ ...primaryButtonSx, minHeight: 36 }}
          >
            Join now
          </Button>
        ) : (
          <Typography sx={{
            fontFamily: font, fontSize: "12px", color: textSecondary,
            backgroundColor: "#F5F2EF", borderRadius: "8px", px: 1.25, py: 0.85, textAlign: "center",
          }}>
            {/* Both windows are ten minutes now (utils/session_join_window.js),
                so the instructor no longer gets in first and must not be told
                they do. */}
            You can join in <Box component="span" sx={{ fontWeight: 800, color: textPrimary }}>{countdown}</Box>
          </Typography>
        )}

        {isOpen && session.isInstructor && (
          <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: success, mt: 0.75, textAlign: "center" }}>
            Recording starts when you join
          </Typography>
        )}
      </Box>

    </>
  );
}
